import type { StepImageDimensions, ZoneConfig, ZoneImageState } from '../types';
import {
  logError,
  loadImage,
  setupCanvas,
  calculateImageScaling,
  renderUserImageWithMask,
  renderUserImageWithoutMask
} from './canvasRenderer';
import { loadPNGMask, calculatePNGMaskPositioning, applyPNGClipping, applyPNGMaskClipping } from './pngProcessor';
import { renderAllZones } from './multiZoneRenderer';
import { supportsWebP } from '../../../utilities/webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

// Simple memoization for canvas previews
const canvasPreviewMemo = new Map<string, string>();

/**
 * Canvas Preview System
 *
 * Reuses the same canvas logic from save handlers for live preview
 * This ensures the preview matches exactly what will be saved
 */

export interface CanvasPreviewOptions {
  imageUrl: string;
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;
  stepImageDimensions: StepImageDimensions | null;
  uploadType: 'photo' | 'engraving';
  processedSrc: string | null;
  maskSvgBlob: Blob | null;
  positioning?: { x: number; y: number; scale: number; rotation: number };
  containerWidth: number;
  containerHeight: number;
  showStarMapSlot?: boolean;
  // Multi-zone options
  zones?: ZoneConfig[];
  zoneImages?: Map<number, ZoneImageState>;
  isMultiZoneMode?: boolean;
  maskImageConfig?: { url: string; width: number; height: number };
}

/**
 * Create a canvas preview that matches the save handler output
 */
export const createCanvasPreview = async (options: CanvasPreviewOptions): Promise<string> => {
  const {
    imageUrl,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    stepImageDimensions,
    uploadType,
    processedSrc,
    maskSvgBlob,
    positioning,
    showStarMapSlot = false
  } = options;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  try {
    // Load the user's uploaded image
    const uploadedImg = new Image();
    uploadedImg.crossOrigin = 'anonymous';
    uploadedImg.src = processedSrc || imageUrl;

    await new Promise<void>((resolve, reject) => {
      uploadedImg.onload = () => resolve();
      uploadedImg.onerror = () => reject(new Error('Failed to load uploaded image'));
    });

    // Calculate standardized dimensions based on step image
    const standardWidth = 1250;
    const standardHeight = stepImageDimensions
      ? (standardWidth * stepImageDimensions.height) / stepImageDimensions.width
      : standardWidth;

    // Set canvas dimensions to standardized values
    canvas.width = standardWidth;
    canvas.height = standardHeight;

    // Get actual canvas dimensions (browser may round)
    const actualWidth = canvas.width;
    const actualHeight = canvas.height;

    // Fill with white background (this will show in areas outside the mask)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate image scaling to fit within canvas while maintaining aspect ratio
    const imageAspectRatio = uploadedImg.width / uploadedImg.height;
    let scaledWidth: number;
    let scaledHeight: number;

    if (imageAspectRatio > standardWidth / standardHeight) {
      // Image is wider - constrain by width to fit within canvas
      scaledWidth = standardWidth;
      scaledHeight = standardWidth / imageAspectRatio;
    } else {
      // Image is taller - constrain by height to fit within canvas
      scaledHeight = standardHeight;
      scaledWidth = standardHeight * imageAspectRatio;
    }

    // Apply PNG masking if mask image data is available
    if (maskSvgBlob) {
      try {
        // Load the PNG mask image
        const maskImage = await loadPNGMask({ maskSvgBlob });

        // Calculate mask positioning
        const maskPositioning = calculatePNGMaskPositioning({
          containerWidth: actualWidth,
          containerHeight: actualHeight,
          positioning
        });

        // Set up PNG clipping context (but don't apply mask yet)
        const clippingResult = applyPNGClipping({
          ctx,
          maskImage,
          positioning: maskPositioning,
          debugMode: false
        });

        if (clippingResult.success) {
          // Apply user transformations
          ctx.save();
          // Move to the mask center position first
          ctx.translate(maskPositioning.maskCenterX, maskPositioning.maskCenterY);
          // Then apply user transformations
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.scale(scale * (flipX ? -1 : 1), scale);
          ctx.translate(offsetX, offsetY);

          // Apply engraving filters if needed
          // For star map, always apply blend mode (even if uploadType is 'photo')
          if (uploadType === 'engraving' || showStarMapSlot) {
            // For star map, apply blend mode only (no contrast)
            if (showStarMapSlot) {
              ctx.globalCompositeOperation = 'multiply';
            } else {
              ctx.filter = 'grayscale(100%) contrast(300%)';
              ctx.globalCompositeOperation = 'multiply';
            }
          }

          // Draw the user image
          ctx.drawImage(uploadedImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);

          // Restore user transformations
          ctx.restore();

          // NOW apply the mask to clip the user's image
          applyPNGMaskClipping({ ctx, maskImage, positioning: maskPositioning });

          // Ensure white background is maintained in areas outside the mask
          // Fill any transparent areas with white background
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
        } else {
          // Draw user image without mask
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.scale(scale * (flipX ? -1 : 1), scale);
          ctx.translate(offsetX, offsetY);

          if (uploadType === 'engraving') {
            ctx.filter = 'grayscale(100%) contrast(300%)';
            ctx.globalCompositeOperation = 'multiply';
          }

          ctx.drawImage(uploadedImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          ctx.restore();
        }
      } catch (error) {
        console.error('❌ Error processing PNG mask for preview:', error);
        // Draw user image without mask
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(scale * (flipX ? -1 : 1), scale);
        ctx.translate(offsetX, offsetY);

        if (uploadType === 'engraving') {
          ctx.filter = 'grayscale(100%) contrast(300%)';
          ctx.globalCompositeOperation = 'multiply';
        }

        ctx.drawImage(uploadedImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();
      }
    } else {
      // No mask image available, draw user image without mask
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale * (flipX ? -1 : 1), scale);
      ctx.translate(offsetX, offsetY);

      if (uploadType === 'engraving') {
        ctx.filter = 'grayscale(100%) contrast(300%)';
        ctx.globalCompositeOperation = 'multiply';
      }

      ctx.drawImage(uploadedImg, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
      ctx.restore();
    }

    // Convert canvas to data URL for preview
    return canvas.toDataURL(imageType, 0.9);
  } catch (error) {
    console.error('❌ Error creating canvas preview:', error);
    throw error;
  }
};

/**
 * Create a scaled canvas preview for the editing interface
 * This maintains the same logic but scales to fit the preview container
 */
export const createScaledCanvasPreview = async (options: CanvasPreviewOptions): Promise<string> => {
  const {
    imageUrl,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    stepImageDimensions,
    uploadType,
    processedSrc,
    maskSvgBlob,
    positioning,
    showStarMapSlot = false,
    zones = [],
    zoneImages = new Map(),
    isMultiZoneMode = false,
    maskImageConfig
  } = options;

  // Create cache key based on all parameters
  // For multi-zone, include all zone transformations in the cache key
  let cacheKey: string;
  if (isMultiZoneMode && zones.length > 0) {
    const zoneKeys = zones
      .map((zone) => {
        const zoneImage = zoneImages.get(zone.index);
        if (!zoneImage) return '';
        return `${zone.index}-${zoneImage.rotation}-${zoneImage.scale}-${zoneImage.flipX}-${zoneImage.offsetX}-${zoneImage.offsetY}`;
      })
      .filter(Boolean)
      .join('|');
    cacheKey = `multi-zone-${zoneKeys}-${uploadType}-${maskSvgBlob ? 'masked' : 'unmasked'}-${positioning?.x || 50}-${positioning?.y || 50}-${positioning?.scale || 100}`;
  } else {
    cacheKey = `${processedSrc || imageUrl}-${rotation}-${scale}-${flipX}-${offsetX}-${offsetY}-${uploadType}-${maskSvgBlob ? 'masked' : 'unmasked'}-${positioning?.x || 50}-${positioning?.y || 50}-${positioning?.scale || 100}`;
  }

  // Check if we have a cached result
  if (canvasPreviewMemo.has(cacheKey)) {
    return canvasPreviewMemo.get(cacheKey)!;
  }

  try {
    // Setup canvas with standardized dimensions
    const { canvas, ctx, actualWidth, actualHeight } = setupCanvas({ stepImageDimensions });

    // Initialize canvas with white background for multi-zone mode
    if (isMultiZoneMode) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    if (isMultiZoneMode && zones.length > 0 && zoneImages.size > 0 && maskSvgBlob && maskImageConfig) {
      // Multi-zone mode: render all zones
      await renderAllZones(ctx, {
        zones,
        zoneImages,
        stepImageDimensions,
        uploadType,
        maskSvgBlob,
        maskImageConfig,
        positioning,
        showStarMapSlot,
        actualWidth,
        actualHeight
      });
    } else if (!isMultiZoneMode && imageUrl) {
      // Single zone mode: existing behavior
      // Load the user's uploaded image
      const uploadedImg = await loadImage({ src: processedSrc || imageUrl });

      // Calculate image scaling
      const { scaledWidth, scaledHeight } = calculateImageScaling({
        image: uploadedImg,
        canvasWidth: actualWidth,
        canvasHeight: actualHeight
      });

      // Render with or without mask
      if (maskSvgBlob) {
        await renderUserImageWithMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
          rotation,
          scale,
          flipX,
          offsetX,
          offsetY,
          uploadType,
          showStarMapSlot,
          maskSvgBlob,
          positioning,
          actualWidth,
          actualHeight
        });
      } else {
        renderUserImageWithoutMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
          rotation,
          scale,
          flipX,
          offsetX,
          offsetY,
          uploadType,
          showStarMapSlot
        });
      }
    }

    // Convert canvas to data URL for preview
    const dataUrl = canvas.toDataURL(imageType, 0.9);

    // Cache the result
    canvasPreviewMemo.set(cacheKey, dataUrl);

    return dataUrl;
  } catch (error) {
    logError('Error creating canvas preview', error, {
      isMultiZoneMode,
      imageUrl: !!imageUrl,
      hasMask: !!maskSvgBlob,
      uploadType
    });
    throw error;
  }
};
